import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSave,
  faTimes,
  faArrowLeft,
  faMapMarkerAlt,
  faWeightHanging,
  faUser,
  faPhone,
  faNoteSticky,
  faChevronRight,
  faChevronLeft,
  faInfoCircle,
  faCheck,
  faCreditCard,
  faMoneyBillWave,
  faGlobe,
  faTruck,
  faBox,
  faEnvelope,
  faArrowRight,
  faPaperPlane,
  faNairaSign,
  faHome,
  faBoxOpen,
  faShippingFast
} from '@fortawesome/free-solid-svg-icons';
import { shipmentService } from '../../../../services/shipmentService';
import { userService } from '../../../../services/userService';
import { paymentsService } from '../../../../services/paymentsService';
import { configService } from '../../../../services/configService';
import { calculatePrice as calculateUnifiedPrice, PROCESSING_FEE } from '../../../../utils/pricing';
import { useUIStore } from '../../../../store/uiStore';
import useWelcomeOnce from '../../../../hooks/useWelcomeOnce';

const NewShipment = ({ hideLocal = false }) => {
  const navigate = useNavigate();
  const { pushToast } = useUIStore();
  useWelcomeOnce('user_new_shipment', 'Welcome! We auto-save your form as you type.');
  const [activeStep, setActiveStep] = useState(0);
  const CLIENT_FORM_KEY = 'client_form_data';
  const [formData, setFormData] = useState({
    senderName: '',
    senderNumber: '',
    senderEmail: '',
    senderAlternativeNumber: '',
    senderAddress: '',
    senderState: '',
    senderDescription: '',
    receiverName: '',
    receiverNumber: '',
    receiverEmail: '',
    receiverAlternativeNumber: '',
    receiverAddress: '',
    receiverState: '',
    receiverNote: '',
    weight: '',
    packageType: 'package',
    serviceType: '',
    pickupLocation: '',
    email: '',
    numberOfItems: '1',
    country: '',
    paymentType: '',
    deliveryType: 'pickup',
    internationalSubtype: '',
    packageSize: 'small',
    height: '',
    width: '',
    contentList: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [submittedTrackingCode, setSubmittedTrackingCode] = useState('');
  const [submittedStatus, setSubmittedStatus] = useState('');
  const lastPriceRef = useRef(null);
  const prevServiceTypeRef = useRef('');

  // Refs for scroll-to-error behavior
  const serviceTypeRef = useRef(null);
  const internationalSubtypeRef = useRef(null);
  const senderNameRef = useRef(null);
  const senderNumberRef = useRef(null);
  const senderAddressRef = useRef(null);
  const receiverNameRef = useRef(null);
  const receiverNumberRef = useRef(null);
  const receiverEmailRef = useRef(null);
  const receiverAddressRef = useRef(null);
  const receiverStateRef = useRef(null);
  const destinationRef = useRef(null);
  const countryRef = useRef(null);
  const weightRef = useRef(null);
  const numberOfItemsRef = useRef(null);
  const heightRef = useRef(null);
  const widthRef = useRef(null);
  const contentListRef = useRef(null);
  const paymentTypeRef = useRef(null);
  const addressTipShownRef = useRef(false);

  const scrollToField = (key) => {
    const map = {
      serviceType: serviceTypeRef,
      internationalSubtype: internationalSubtypeRef,
      senderName: senderNameRef,
      senderNumber: senderNumberRef,
      senderAddress: senderAddressRef,
      receiverName: receiverNameRef,
      receiverNumber: receiverNumberRef,
      receiverEmail: receiverEmailRef,
      receiverAddress: receiverAddressRef,
      receiverState: receiverStateRef,
      country: countryRef,
      weight: weightRef,
      numberOfItems: numberOfItemsRef,
      height: heightRef,
      width: widthRef,
      contentList: contentListRef,
      paymentType: paymentTypeRef,
    };
    const ref = map[key];
    const node = ref && ref.current;
    if (node) {
      try {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof node.focus === 'function') setTimeout(() => node.focus({ preventScroll: true }), 120);
      } catch { }
    }
  };

  const nigerianStates = [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT (Abuja)', 'Gombe',
    'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos',
    'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto',
    'Taraba', 'Yobe', 'Zamfara'
  ];

  const internationalCountries = [
    'United States', 'United Kingdom', 'Canada', 'China', 'Germany', 'France',
    'Italy', 'Spain', 'Netherlands', 'India', 'South Africa', 'United Arab Emirates',
    'Saudi Arabia', 'Malaysia', 'Singapore', 'Australia', 'Brazil', 'Ghana',
    'Kenya', 'Egypt', 'Turkey', 'Japan', 'South Korea', 'Mexico', 'Nigeria', 'Others'
  ];

  const [pricingConfig, setPricingConfig] = useState(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(CLIENT_FORM_KEY) || 'null');
      if (saved && typeof saved === 'object') {
        setFormData(prev => ({ ...prev, ...saved }));
        try { pushToast('Draft restored', 'info'); } catch { }
      }
    } catch { }
  }, []);

  // Fetch user profile to pre-fill sender details
  useEffect(() => {
    (async () => {
      try {
        const data = await userService.getMe();
        const user = data?.user || (JSON.parse(localStorage.getItem('user') || '{}'));
        if (user) {
          setFormData(prev => ({
            ...prev,
            senderName: prev.senderName || user.name || '',
            senderNumber: prev.senderNumber || user.phone || '',
            senderEmail: prev.senderEmail || user.email || '',
            senderAddress: prev.senderAddress || user.address || '',
            senderState: prev.senderState || user.state || '',
            email: prev.email || user.email || '',
            pickupLocation: prev.pickupLocation || user.address || ''
          }));
        }
      } catch (err) {
        console.warn('Failed to fetch user profile:', err);
      }
    })();
  }, []);

  // Fetch pricing config - simplified, we only need the raw config now
  useEffect(() => {
    (async () => {
      try {
        const resp = await configService.getPricingConfig();
        if (resp?.success && resp.config) {
          setPricingConfig(resp.config);
        }
      } catch (err) {
        console.error('Failed to fetch pricing config:', err);
        // pricingConfig remains null, pricing.js will use its built-in defaults
      }
    })();
  }, []);

  const baseServiceTypes = [
    {
      value: 'local',
      label: 'Local Delivery',
      icon: faHome,
      description: 'Within Lagos',
      color: 'blue'
    },
    {
      value: 'interstate',
      label: 'Interstate',
      icon: faTruck,
      description: 'Across Nigeria',
      color: 'purple'
    },
    {
      value: 'international',
      label: 'International',
      icon: faGlobe,
      description: 'Worldwide',
      color: 'green'
    }
  ];
  const serviceTypes = hideLocal ? baseServiceTypes.filter(t => t.value !== 'local') : baseServiceTypes;

  useEffect(() => {
    if (hideLocal && formData.serviceType === 'local') {
      setFormData(prev => {
        const next = { ...prev, serviceType: '' };
        try { localStorage.setItem(CLIENT_FORM_KEY, JSON.stringify(next)); } catch { }
        return next;
      });
      try { pushToast('Local delivery is available in the Ecommerce Local Booking flow.', 'info', 2500); } catch { }
    }
  }, [hideLocal, formData.serviceType]);

  const internationalSubtypes = [
    {
      value: 'express',
      label: 'Express',
      icon: faGlobe,
      description: 'Fast international door-to-door',
      color: 'green'
    },
    {
      value: 'cargo',
      label: 'Cargo',
      icon: faBox,
      description: 'Economy cargo service',
      color: 'purple'
    }
  ];

  const validatePhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('0')) return true;
    if (cleaned.length === 10) return true;
    if (phone.startsWith('+234') && cleaned.length === 13) return true;
    return false;
  };

  // SIMPLIFIED: Now relies entirely on pricing.js with its built-in defaults
  const calculateShippingPrice = () => {
    if (!formData.serviceType) return null;

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userRole = user.role === 'ecommerce' ? 'ecommerce' : 'user';
    const weight = parseFloat(formData.weight) || 0;

    const payload = {
      serviceType: formData.serviceType,
      subtype: formData.internationalSubtype,
      senderState: formData.senderState,
      receiverState: formData.receiverState,
      country: formData.country,
      weight,
      deliveryType: formData.deliveryType,
      userRole: userRole,
      packageSize: formData.packageSize
    };

    // Pass pricingConfig if available, otherwise empty object
    // pricing.js will use its built-in DEFAULTS for any missing fields
    return calculateUnifiedPrice(payload, pricingConfig || {});
  };

  // Recalculate price when dependencies change
  useEffect(() => {
    if (activeStep === 2) {
      const price = calculateShippingPrice();
      setCalculatedPrice(price);
    }
  }, [formData, activeStep, pricingConfig]);

  // Price change hint
  useEffect(() => {
    if (typeof calculatedPrice === 'number' && calculatedPrice > 0) {
      if (lastPriceRef.current !== calculatedPrice) {
        lastPriceRef.current = calculatedPrice;
        pushToast('Shipping cost updated', 'info', 1200);
      }
    }
  }, [calculatedPrice, pushToast]);

  const getPaymentTypes = () => {
    if (formData.serviceType === 'international') {
      return [
        { value: 'online', label: 'Online Payment', icon: faCreditCard, description: 'Pay now with card', color: 'green' }
      ];
    }
    return [
      { value: 'onDelivery', label: 'Pay on Delivery', icon: faMoneyBillWave, description: 'Cash on delivery', color: 'orange' },
      { value: 'online', label: 'Online Payment', icon: faCreditCard, description: 'Pay now with card', color: 'green' }
    ];
  };

  // Handle service type changes - reset payment and delivery type as needed
  useEffect(() => {
    setFormData(prev => {
      let next = { ...prev };
      let changed = false;

      // International must use online payment
      if (formData.serviceType === 'international' && next.paymentType !== 'online') {
        next.paymentType = 'online';
        changed = true;
      }

      // Local and international don't have doorstep option, reset to pickup
      if (['local', 'international'].includes(formData.serviceType) && next.deliveryType !== 'pickup') {
        next.deliveryType = 'pickup';
        changed = true;
      }

      if (changed) {
        try { localStorage.setItem(CLIENT_FORM_KEY, JSON.stringify(next)); } catch { }
        return next;
      }
      return prev;
    });

    if (formData.serviceType === 'international' && prevServiceTypeRef.current !== formData.serviceType && prevServiceTypeRef.current !== '') {
      pushToast('International shipments use online payment automatically', 'info', 1500);
    }
    prevServiceTypeRef.current = formData.serviceType || '';
  }, [formData.serviceType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => { const next = { ...prev, [name]: value }; try { localStorage.setItem(CLIENT_FORM_KEY, JSON.stringify(next)); } catch { }; return next; });
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  ['local', 'interstate'].includes(formData.serviceType) &&
    formData.senderState &&
    formData.senderState !== 'Lagos' &&
    !['Island', 'Mainland', 'Outskirt'].includes(formData.senderState);

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 0) {
      if (!formData.serviceType) newErrors.serviceType = 'Please select a service type';

      if (isOutsideLagos) {
        newErrors.senderState = 'Local and interstate delivery are only available for pickups from Lagos';
      }

      // Sender validations
      if (!formData.senderName.trim()) newErrors.senderName = 'Sender name is required';
      if (!formData.senderNumber.trim()) newErrors.senderNumber = 'Sender number is required';
      if (!validatePhoneNumber(formData.senderNumber)) newErrors.senderNumber = 'Invalid sender phone number';
      if (!formData.senderAddress.trim()) newErrors.senderAddress = 'Sender address is required';
      if (!formData.senderState) newErrors.senderState = 'Sender state is required';

      // Receiver validations
      if (!formData.receiverName.trim()) newErrors.receiverName = 'Receiver name is required';
      if (!formData.receiverNumber.trim()) newErrors.receiverNumber = 'Receiver number is required';
      if (!validatePhoneNumber(formData.receiverNumber)) newErrors.receiverNumber = 'Invalid receiver phone number';

      const isLocalOrInternational = ['local', 'international'].includes(formData.serviceType);
      const isPickup = formData.deliveryType === 'pickup';
      const isInternationalCargo = formData.serviceType === 'international' && formData.internationalSubtype === 'cargo';

      if (isLocalOrInternational || (!isPickup && !isInternationalCargo)) {
        if (!formData.receiverAddress.trim()) {
          newErrors.receiverAddress = isLocalOrInternational
            ? 'Drop-off address is required'
            : 'Receiver address is required for doorstep delivery';
        } else if (formData.receiverAddress.trim().length < 10) {
          newErrors.receiverAddress = 'Please provide a detailed address';
        }
      }

      if (formData.serviceType === 'local' && !formData.receiverState) {
        newErrors.receiverState = 'Please select a city';
      }
      if (formData.serviceType === 'interstate' && !formData.receiverState) {
        newErrors.receiverState = 'Please select a state';
      }
      if (formData.serviceType === 'international' && !formData.country) {
        newErrors.country = 'Please select a country';
      }
      if (formData.serviceType === 'international' && !formData.internationalSubtype) {
        newErrors.internationalSubtype = 'Please select subtype';
      }
    }

    if (step === 1) {
      if (!formData.weight.trim()) {
        newErrors.weight = 'Weight is required';
      } else {
        const w = parseFloat(formData.weight);
        if (formData.serviceType === 'international' && formData.internationalSubtype === 'cargo' && w < 10) {
          newErrors.weight = 'Minimum weight for international cargo is 10kg';
        }
      }
      if (!formData.numberOfItems.trim()) newErrors.numberOfItems = 'Number of items is required';
      if (!formData.contentList.trim()) newErrors.contentList = 'List of content is required';

      if (formData.serviceType === 'international' && formData.internationalSubtype === 'express') {
        if (!formData.height.trim()) newErrors.height = 'Height is required';
        if (!formData.width.trim()) newErrors.width = 'Width is required';
        if (!formData.contentList.trim()) newErrors.contentList = 'List of content is required';
      }
    }

    if (step === 2) {
      if (!formData.paymentType) newErrors.paymentType = 'Please select a payment method';
    }

    setErrors(newErrors);
    if (step === 0) {
      if ((newErrors.receiverAddress || (formData.receiverAddress && formData.receiverAddress.trim().length < 10)) && !addressTipShownRef.current) {
        addressTipShownRef.current = true;
        try { pushToast('Tip: Add landmarks or nearest bus-stop for better delivery.', 'info', 3000); } catch { }
      }
    }
    const keys = Object.keys(newErrors);
    if (keys.length) {
      const order0 = ['serviceType', 'internationalSubtype', 'senderName', 'senderNumber', 'senderAddress', 'senderState', 'receiverName', 'receiverNumber', 'receiverAddress', 'receiverState', 'country'];
      const order1 = ['weight', 'numberOfItems', 'height', 'width', 'contentList'];
      const order2 = ['paymentType'];
      const ordered = step === 0 ? order0 : step === 1 ? order1 : order2;
      const target = ordered.find(k => newErrors[k] !== undefined) || keys[0];
      scrollToField(target);
      return false;
    }
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => Math.min(prev + 1, 2));
    }
  };

  const prevStep = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep(activeStep)) {
      if (!(typeof calculatedPrice === 'number') || calculatedPrice <= 0) {
        setErrors(prev => ({ ...prev, submit: 'Unable to calculate shipping cost' }));
        return;
      }
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const normalizedPaymentType = formData.paymentType === 'online' ? 'payNow' : 'cashOnDelivery';
        const payload = {
          ...formData,
          price: calculatedPrice,
          paymentType: normalizedPaymentType,
          paymentStatus: 'Pending'
        };
        const result = await shipmentService.createShipment(payload, token);
        const newShipment = result?.shipment || result;
        setLoading(false);
        setSubmittedTrackingCode(newShipment?.trackingCode || '');
        setSubmittedStatus(newShipment?.status || '');
        setRequestSent(true);
        try { localStorage.removeItem(CLIENT_FORM_KEY); } catch { }

        // Update user profile with latest sender info used for booking
        try {
          await userService.updateMe({
            name: formData.senderName,
            phone: formData.senderNumber,
            address: formData.senderAddress,
            state: formData.senderState
          });
          // Also update local storage user object to reflect changes immediately
          const localUser = JSON.parse(localStorage.getItem('user') || '{}');
          const updatedLocalUser = {
            ...localUser,
            name: formData.senderName,
            phone: formData.senderNumber,
            address: formData.senderAddress,
            state: formData.senderState
          };
          localStorage.setItem('user', JSON.stringify(updatedLocalUser));
        } catch (profileErr) {
          console.warn('Silent profile update failed:', profileErr);
        }

        if (formData.paymentType === 'online' && (newShipment?.paymentStatus !== 'Paid')) {
          try {
            const init = await paymentsService.initialize({
              email: formData.email,
              amount: calculatedPrice,
              shipmentId: newShipment?._id || newShipment?.id
            }, token);
            const authUrl = init?.data?.authorization_url;
            if (authUrl) {
              window.location.href = authUrl;
            } else {
              throw new Error('Unable to start online payment. Please try again.');
            }
          } catch (payErr) {
            alert(payErr?.response?.data?.msg || payErr?.message || 'Payment initialization failed');
            navigate('/dashboard/user/shipments');
          }
        } else {
          setTimeout(() => navigate('/dashboard/user/shipments'), 1500);
        }
      } catch (err) {
        setLoading(false);
        setErrors(prev => ({ ...prev, submit: err?.message || 'Failed to create shipment' }));
      }
    }
  };

  const isOutsideLagos = ['local', 'interstate'].includes(formData.serviceType) &&
    formData.senderState &&
    formData.senderState !== 'Lagos' &&
    !['Island', 'Mainland', 'Outskirt'].includes(formData.senderState);

  // FIXED: Doorstep fee now matches pricing.js logic exactly
  // pricing.js uses: Number(config?.doorstepFee ?? DEFAULTS.doorstepFee) where DEFAULTS.doorstepFee = 5000
  const getDoorstepDisplayFee = () => {
    if (formData.serviceType !== 'interstate' || formData.deliveryType !== 'doorstep') {
      return 0;
    }
    return Number(pricingConfig?.doorstepFee ?? 5000) || 0;
  };

  const doorstepDisplayFee = getDoorstepDisplayFee();

  const priceBreakdown = typeof calculatedPrice === 'number' && calculatedPrice > 0
    ? {
      processingFee: PROCESSING_FEE,
      doorstepFee: doorstepDisplayFee,
      baseRate: Math.max(0, calculatedPrice - PROCESSING_FEE - doorstepDisplayFee)
    }
    : null;

  const supportedInterstateDestinations = [
    'Abeokuta', 'Ado Ekiti', 'Osogbo', 'Ibadan', 'Ilorin', 'Akure', // Zone 1
    'Aba', 'Owerri', 'Enugu', 'Onitsha', 'Awka', // Zone 2
    'Asaba', 'Warri', 'Benin', // Zone 3
    'Abuja', 'Uyo', 'Port Harcourt', 'Bayelsa', // Zone 4
    'Kano', 'Kaduna', 'Makurdi', 'Minna', 'Bauchi' // Zone 5
  ].sort();

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <div className="space-y-6">
            {/* Service Type - Compact Cards */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Delivery Type *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" ref={serviceTypeRef}>
                {serviceTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData(prev => { const next = { ...prev, serviceType: type.value }; try { localStorage.setItem(CLIENT_FORM_KEY, JSON.stringify(next)); } catch { }; return next; })}
                    className={`relative p-4 rounded-xl border-2 transition-all ${formData.serviceType === type.value
                      ? `border-${type.color}-500 bg-${type.color}-50`
                      : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <div className="flex sm:flex-col items-center sm:text-center gap-4 sm:gap-0">
                      <div className={`w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center sm:mb-2 ${formData.serviceType === type.value
                        ? `bg-${type.color}-100`
                        : 'bg-gray-100'
                        }`}>
                        <FontAwesomeIcon
                          icon={type.icon}
                          className={`text-xl ${formData.serviceType === type.value
                            ? `text-${type.color}-600`
                            : 'text-gray-400'
                            }`}
                        />
                      </div>
                      <div className="text-left sm:text-center min-w-0">
                        <h4 className="font-semibold text-sm text-gray-800">{type.label}</h4>
                        <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                      </div>
                    </div>
                    {formData.serviceType === type.value && (
                      <div className="absolute top-2 right-2">
                        <div className={`w-5 h-5 rounded-full bg-${type.color}-500 flex items-center justify-center`}>
                          <FontAwesomeIcon icon={faCheck} className="text-white text-[10px]" />
                        </div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              {errors.serviceType && <p className="mt-2 text-sm text-red-600">{errors.serviceType}</p>}
              {formData.serviceType === 'international' && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Select International Shipment Type *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" ref={internationalSubtypeRef}>
                    {internationalSubtypes.map((st) => (
                      <button
                        key={st.value}
                        type="button"
                        onClick={() => setFormData(prev => { const next = { ...prev, internationalSubtype: st.value }; try { localStorage.setItem(CLIENT_FORM_KEY, JSON.stringify(next)); } catch { }; return next; })}
                        className={`relative p-4 rounded-xl border-2 transition-all ${formData.internationalSubtype === st.value
                          ? `border-${st.color}-500 bg-${st.color}-50`
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center mr-3 ${formData.internationalSubtype === st.value
                            ? `bg-${st.color}-100`
                            : 'bg-gray-100'
                            }`}>
                            <FontAwesomeIcon
                              icon={st.icon}
                              className={`text-lg ${formData.internationalSubtype === st.value
                                ? `text-${st.color}-600`
                                : 'text-gray-400'
                                }`}
                            />
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <h4 className="font-semibold text-sm text-gray-800 truncate">{st.label}</h4>
                            <p className="text-xs text-gray-500 truncate">{st.description}</p>
                          </div>
                          {formData.internationalSubtype === st.value && (
                            <div className={`w-5 h-5 rounded-full bg-${st.color}-500 flex items-center justify-center ml-2`}>
                              <FontAwesomeIcon icon={faCheck} className="text-white text-[10px]" />
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  {errors.internationalSubtype && <p className="mt-2 text-sm text-red-600">{errors.internationalSubtype}</p>}
                </div>
              )}
            </div>

            {/* Sender Details - New Section */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faUser} className="mr-2 text-blue-500" />
                Sender Information (From Profile)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Sender Name *</label>
                  <input
                    type="text"
                    name="senderName"
                    value={formData.senderName}
                    onChange={handleInputChange}
                    ref={senderNameRef}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.senderName ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Your full name"
                  />
                  {errors.senderName && <p className="mt-1 text-xs text-red-600">{errors.senderName}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Sender Phone *</label>
                  <input
                    type="tel"
                    name="senderNumber"
                    value={formData.senderNumber}
                    onChange={handleInputChange}
                    ref={senderNumberRef}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.senderNumber ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="080XXXXXXXX"
                  />
                  {errors.senderNumber && <p className="mt-1 text-xs text-red-600">{errors.senderNumber}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Sender Address *</label>
                  <input
                    type="text"
                    name="senderAddress"
                    value={formData.senderAddress}
                    onChange={handleInputChange}
                    ref={senderAddressRef}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.senderAddress ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Your pickup address"
                  />
                  {errors.senderAddress && <p className="mt-1 text-xs text-red-600">{errors.senderAddress}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    {formData.serviceType === 'local' ? 'Sender Location Type *' : (formData.serviceType === 'international' ? 'Sender Country *' : 'Sender State *')}
                  </label>
                  <select
                    name="senderState"
                    value={formData.senderState}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.senderState ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    {formData.serviceType === 'local' ? (
                      <>
                        <option value="">Select Location Type</option>
                        <option value="Mainland">Mainland</option>
                        <option value="Island">Island</option>
                        <option value="Outskirt">Outskirt</option>
                      </>
                    ) : (
                      <>
                        <option value="">Select {formData.serviceType === 'international' ? 'country' : 'state'}</option>
                        {formData.serviceType === 'international'
                          ? internationalCountries.map(c => <option key={c} value={c}>{c}</option>)
                          : nigerianStates.map(state => <option key={state} value={state}>{state}</option>)
                        }
                      </>
                    )}
                  </select>
                  {errors.senderState && <p className="mt-1 text-xs text-red-600">{errors.senderState}</p>}
                  {isOutsideLagos && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                      <FontAwesomeIcon icon={faInfoCircle} className="text-amber-600 mt-0.5" />
                      <p className="text-xs text-amber-800 font-medium">
                        Local and interstate delivery are currently only available for pickups from <span className="font-bold">Lagos</span>.
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Sender Email (Optional)</label>
                  <input
                    type="email"
                    name="senderEmail"
                    value={formData.senderEmail}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 text-sm border rounded-lg border-gray-300"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
              <p className="mt-3 text-[10px] text-gray-500 italic flex items-center">
                <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                Changes made here will also be saved to your profile upon booking.
              </p>
            </div>

            {/* Receiver Details - Compact Form */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faUser} className="mr-2 text-red-500" />
                Receiver Information
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="receiverName"
                      value={formData.receiverName}
                      onChange={handleInputChange}
                      ref={receiverNameRef}
                      className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.receiverName ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter receiver's name"
                    />
                    {errors.receiverName && <p className="mt-1 text-xs text-red-600">{errors.receiverName}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Phone Number *</label>
                    <input
                      type="tel"
                      name="receiverNumber"
                      value={formData.receiverNumber}
                      onChange={handleInputChange}
                      ref={receiverNumberRef}
                      className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.receiverNumber ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="080XXXXXXXX"
                    />
                    {errors.receiverNumber && <p className="mt-1 text-xs text-red-600">{errors.receiverNumber}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Email (Optional)</label>
                    <input
                      type="email"
                      name="receiverEmail"
                      value={formData.receiverEmail}
                      onChange={handleInputChange}
                      ref={receiverEmailRef}
                      className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.receiverEmail ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="receiver@example.com"
                    />
                    {errors.receiverEmail && <p className="mt-1 text-xs text-red-600">{errors.receiverEmail}</p>}
                  </div>
                </div>

                {!['local', 'international'].includes(formData.serviceType) && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Delivery Option</label>
                    <div className="flex gap-4 mb-4">
                      <label className={`flex-1 flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.deliveryType === 'doorstep' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}>
                        <input
                          type="radio"
                          name="deliveryType"
                          value="doorstep"
                          checked={formData.deliveryType === 'doorstep'}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <FontAwesomeIcon icon={faTruck} className="mr-2" />
                        <span className="text-sm font-semibold">Doorstep Delivery</span>
                      </label>
                      <label className={`flex-1 flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.deliveryType === 'pickup' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}>
                        <input
                          type="radio"
                          name="deliveryType"
                          value="pickup"
                          checked={formData.deliveryType === 'pickup'}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <FontAwesomeIcon icon={faHome} className="mr-2" />
                        <span className="text-sm font-semibold">Hub Pickup</span>
                      </label>
                    </div>
                  </div>
                )}

                {(['local', 'international'].includes(formData.serviceType) || formData.deliveryType === 'doorstep') && (
                  <div className="animate-in fade-in slide-in-from-top-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      {formData.serviceType === 'local' ? 'Drop-off Address or Nearest Bus Stop' : 'Delivery Address'}
                      {formData.serviceType === 'international' && formData.internationalSubtype === 'cargo' ? '' : ' *'}
                    </label>
                    <input
                      type="text"
                      name="receiverAddress"
                      value={formData.receiverAddress}
                      onChange={handleInputChange}
                      ref={receiverAddressRef}
                      className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.receiverAddress ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder={formData.serviceType === 'local' ? "Street address or nearest bus stop" : "Street address, apartment, suite, etc."}
                    />
                    {errors.receiverAddress && <p className="mt-1 text-xs text-red-600">{errors.receiverAddress}</p>}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.serviceType === 'local' && (
                    <div className="relative" ref={destinationRef}>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">Receiver Location Type *</label>
                      <select
                        name="receiverState"
                        value={formData.receiverState}
                        onChange={handleInputChange}
                        ref={receiverStateRef}
                        className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.receiverState ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">Select Location Type</option>
                        <option value="Mainland">Mainland</option>
                        <option value="Island">Island</option>
                        <option value="Outskirt">Outskirt</option>
                      </select>
                      {errors.receiverState && <p className="mt-1 text-xs text-red-600">{errors.receiverState}</p>}
                    </div>
                  )}

                  {formData.serviceType === 'interstate' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">State *</label>
                        <select
                          name="receiverState"
                          value={formData.receiverState}
                          onChange={handleInputChange}
                          ref={receiverStateRef}
                          className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.receiverState ? 'border-red-500' : 'border-gray-300'}`}
                        >
                          <option value="">Select destination</option>
                          {supportedInterstateDestinations.map(dest => (
                            <option key={dest} value={dest}>{dest}</option>
                          ))}
                        </select>
                        {errors.receiverState && <p className="mt-1 text-xs text-red-600">{errors.receiverState}</p>}
                      </div>
                      <div className="md:col-span-2">
                        <label className="flex items-center text-sm font-medium text-gray-700 bg-purple-50 p-3 rounded-lg border border-purple-100 cursor-pointer hover:bg-purple-100 transition-colors">
                          <input
                            type="checkbox"
                            name="isExpress"
                            checked={formData.isExpress}
                            onChange={(e) => setFormData(prev => ({ ...prev, isExpress: e.target.checked }))}
                            className="mr-3 w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                          />
                          <div className="flex-1">
                            <span className="block font-semibold">Express Service</span>
                            <span className="text-xs text-gray-500">Fast-tracked processing & priority handling (Extra Cost)</span>
                          </div>
                          <FontAwesomeIcon icon={faShippingFast} className="text-purple-500 ml-2" />
                        </label>
                      </div>
                    </>
                  )}

                  {formData.serviceType === 'international' && (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">Country *</label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        ref={countryRef}
                        className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.country ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">Select country</option>
                        {internationalCountries.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                      {errors.country && <p className="mt-1 text-xs text-red-600">{errors.country}</p>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faBoxOpen} className="mr-2 text-red-500" />
                Package Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    <FontAwesomeIcon icon={faWeightHanging} className="mr-1" /> Weight (kg) *
                  </label>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    onWheel={(e) => e.target.blur()}
                    ref={weightRef}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.weight ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="e.g., 2.5"
                    step="0.1"
                  />
                  {errors.weight && <p className="mt-1 text-xs text-red-600">{errors.weight}</p>}

                  {/* International Shipment Info Box */}
                  {formData.serviceType === 'international' && (
                    <div className="mt-3 p-4 rounded-xl border-2 transition-all bg-gray-50 border-gray-200">
                      <h4 className="font-bold text-sm text-gray-800 mb-2 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-blue-500" />
                        International {formData.internationalSubtype === 'express' ? 'Express' : 'Cargo'} Information
                      </h4>
                      <div className="space-y-2">
                        {formData.internationalSubtype === 'cargo' ? (
                          <>
                            <p className="text-xs text-gray-600 font-medium italic">Important: Minimum weight for Cargo is 10kg.</p>
                          </>
                        ) : (
                          <>
                            <p className="text-xs text-gray-600">Express delivery typically takes 3-5 business days. Please ensure you provide accurate dimensions (Height & Width) for volumetric weight calculation.</p>
                            <div className="flex items-center gap-2 mt-2 p-2 bg-green-50 rounded-lg border border-green-100">
                              <FontAwesomeIcon icon={faShippingFast} className="text-green-600 text-xs" />
                              <p className="text-[10px] text-green-700 font-medium">Fast-tracked door-to-door worldwide delivery.</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Number of Items *</label>
                  <input
                    type="number"
                    name="numberOfItems"
                    value={formData.numberOfItems}
                    onChange={handleInputChange}
                    onWheel={(e) => e.target.blur()}
                    ref={numberOfItemsRef}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.numberOfItems ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="1"
                    min="1"
                  />
                  {errors.numberOfItems && <p className="mt-1 text-xs text-red-600">{errors.numberOfItems}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-2">Package Size *</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'small', label: 'Small', desc: 'Fits in hand' },
                      { value: 'medium', label: 'Medium', desc: 'Box/Bag size' },
                      { value: 'bulky', label: 'Bulky', desc: 'Large/Heavy' }
                    ].map((size) => (
                      <label
                        key={size.value}
                        className={`flex flex-col items-center p-3 rounded-xl border-2 cursor-pointer transition-all ${formData.packageSize === size.value
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                      >
                        <input
                          type="radio"
                          name="packageSize"
                          value={size.value}
                          checked={formData.packageSize === size.value}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="text-sm font-bold">{size.label}</span>
                        <span className="text-[10px] opacity-60 text-center">{size.desc}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">List of Content / Items *</label>
                  <textarea
                    name="contentList"
                    value={formData.contentList}
                    onChange={handleInputChange}
                    ref={contentListRef}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.contentList ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="e.g. 2 Handbags, 1 Laptop, 5 Shoes"
                    rows="2"
                  ></textarea>
                  {errors.contentList && <p className="mt-1 text-xs text-red-600">{errors.contentList}</p>}
                </div>

                {formData.serviceType === 'international' && formData.internationalSubtype === 'express' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">Height (cm) *</label>
                      <input
                        type="number"
                        name="height"
                        value={formData.height}
                        onChange={handleInputChange}
                        onWheel={(e) => e.target.blur()}
                        ref={heightRef}
                        className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.height ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="e.g. 20"
                      />
                      {errors.height && <p className="mt-1 text-xs text-red-600">{errors.height}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">Width (cm) *</label>
                      <input
                        type="number"
                        name="width"
                        value={formData.width}
                        onChange={handleInputChange}
                        onWheel={(e) => e.target.blur()}
                        ref={widthRef}
                        className={`w-full px-3 py-2.5 text-sm border rounded-lg ${errors.width ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="e.g. 15"
                      />
                      {errors.width && <p className="mt-1 text-xs text-red-600">{errors.width}</p>}
                    </div>
                  </>
                )}

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Pickup Location</label>
                  <input
                    type="text"
                    name="pickupLocation"
                    value={formData.pickupLocation}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 text-sm border rounded-lg border-gray-300 bg-gray-50"
                    readOnly
                  />
                  {isOutsideLagos && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                      <FontAwesomeIcon icon={faInfoCircle} className="text-amber-600 mt-0.5" />
                      <p className="text-xs text-amber-800 font-medium">
                        Pickup from {formData.senderState} is currently unavailable. Please contact <span className="font-bold">07046674299</span> for manual arrangement.
                      </p>
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Additional Notes (Optional)</label>
                  <textarea
                    name="receiverNote"
                    value={formData.receiverNote}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 text-sm border rounded-lg border-gray-300"
                    placeholder="Any special instructions..."
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Quick Summary */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-0.5 mr-3" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">Shipment Summary</p>
                  <div className="mt-2 space-y-1 text-xs text-blue-700">
                    <p><span className="font-medium">From:</span> {formData.senderState}</p>
                    <p><span className="font-medium">To:</span> {formData.receiverState || formData.country || '—'}</p>
                    <p><span className="font-medium">Type:</span> {serviceTypes.find(t => t.value === formData.serviceType)?.label}{formData.serviceType === 'international' && formData.internationalSubtype ? ` • ${formData.internationalSubtype === 'express' ? 'Express' : 'Cargo'}` : ''}{formData.serviceType === 'interstate' && formData.isExpress ? ' • Express' : ''}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {/* Payment Selection - Compact */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Choose Payment Method *
              </label>
              <div className={`grid ${getPaymentTypes().length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-3`} ref={paymentTypeRef}>
                {getPaymentTypes().map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData(prev => { const next = { ...prev, paymentType: type.value }; try { localStorage.setItem(CLIENT_FORM_KEY, JSON.stringify(next)); } catch { }; return next; })}
                    className={`relative p-4 rounded-xl border-2 transition-all ${formData.paymentType === type.value
                      ? `border-${type.color}-500 bg-${type.color}-50`
                      : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <div className="flex items-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-3 ${formData.paymentType === type.value
                        ? `bg-${type.color}-100`
                        : 'bg-gray-100'
                        }`}>
                        <FontAwesomeIcon
                          icon={type.icon}
                          className={`text-xl ${formData.paymentType === type.value
                            ? `text-${type.color}-600`
                            : 'text-gray-400'
                            }`}
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <h4 className="font-semibold text-sm text-gray-800">{type.label}</h4>
                        <p className="text-xs text-gray-500">{type.description}</p>
                      </div>
                      {formData.paymentType === type.value && (
                        <FontAwesomeIcon icon={faCheck} className={`text-${type.color}-600 ml-2`} />
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {errors.paymentType && <p className="mt-2 text-sm text-red-600">{errors.paymentType}</p>}
            </div>

            {/* Price Breakdown - Cleaner Design */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faNairaSign} className="mr-2 text-green-600" />
                Shipping Cost
              </h3>

              <div className="bg-white rounded-lg p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Base Rate</span>
                  <span className="font-medium">
                    {priceBreakdown ? `₦${priceBreakdown.baseRate.toLocaleString()}` : '—'}
                  </span>
                </div>
                {priceBreakdown?.doorstepFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Doorstep Fee</span>
                    <span className="font-medium">₦{priceBreakdown.doorstepFee.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Processing Fee</span>
                  <span className="font-medium">₦{priceBreakdown ? priceBreakdown.processingFee.toLocaleString() : '25'}</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                  <span className="font-semibold text-gray-800">Total Amount</span>
                  <span className="text-2xl font-bold text-green-600">
                    {typeof calculatedPrice === 'number' && calculatedPrice > 0 ? (
                      `₦${calculatedPrice.toLocaleString()}`
                    ) : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Review - Compact Cards */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Review Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-2">FROM</p>
                  <p className="font-semibold text-sm text-gray-800">{formData.senderName}</p>
                  <p className="text-xs text-gray-600 mt-1">{formData.senderAddress}</p>
                  <p className="text-xs text-gray-600">{formData.senderState}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-2">TO</p>
                  <p className="font-semibold text-sm text-gray-800">{formData.receiverName}</p>
                  <p className="text-xs text-gray-600 mt-1">{formData.receiverAddress}</p>
                  <p className="text-xs text-gray-600">
                    {formData.serviceType === 'international' ? formData.country : formData.receiverState}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-2">PACKAGE</p>
                  <p className="text-sm text-gray-800">
                    <span className="font-semibold">{formData.weight} kg</span> • {formData.numberOfItems} item(s)
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {serviceTypes.find(t => t.value === formData.serviceType)?.label}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-2">PAYMENT</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {getPaymentTypes().find(t => t.value === formData.paymentType)?.label}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {formData.paymentType === 'online' ? 'Pay before shipping' : 'Pay upon delivery'}
                  </p>
                </div>
              </div>
            </div>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">{errors.submit}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (requestSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FontAwesomeIcon icon={faCheck} className="text-green-600 text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-3">Success!</h1>
          <p className="text-gray-600 mb-6">Your shipment has been booked successfully</p>

          <div className="bg-gray-50 rounded-xl p-5 mb-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Tracking ID</span>
              <span className="font-semibold">{submittedTrackingCode || '—'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Status</span>
              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">{submittedStatus || '—'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Amount</span>
              <span className="font-semibold text-green-600">
                {typeof calculatedPrice === 'number' && calculatedPrice > 0 ? `₦${calculatedPrice.toLocaleString()}` : '—'}
              </span>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            {formData.paymentType === 'online'
              ? 'Redirecting to payment...'
              : 'You can track your shipment from your dashboard'}
          </p>

          <div className="flex justify-center">
            <div className="w-8 h-8 border-3 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  const steps = ['Details', 'Package', 'Payment'];

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-6">
      {/* Mobile-Optimized Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate('/dashboard/user')}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 className="text-lg font-bold text-gray-900">Book Shipment</h1>
            <div className="w-10"></div>
          </div>

          {/* Compact Progress Bar */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${index < activeStep ? 'bg-green-500 text-white' :
                    index === activeStep ? 'bg-red-500 text-white' :
                      'bg-gray-200 text-gray-500'
                    }`}>
                    {index < activeStep ? <FontAwesomeIcon icon={faCheck} /> : index + 1}
                  </div>
                  <span className={`text-xs mt-1 font-medium ${index === activeStep ? 'text-red-600' : 'text-gray-500'
                    }`}>
                    {step}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${index < activeStep ? 'bg-green-500' : 'bg-gray-200'
                    }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-5 md:p-6">
            {renderStepContent()}
          </div>

          {/* Navigation Buttons */}
          <div className="bg-gray-50 px-5 md:px-6 py-4 flex justify-between gap-3 border-t border-gray-100">
            <button
              type="button"
              onClick={activeStep === 0 ? () => navigate('/dashboard/user') : prevStep}
              className="px-5 py-2.5 border-2 border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-100 flex items-center transition-colors"
            >
              <FontAwesomeIcon icon={activeStep === 0 ? faTimes : faChevronLeft} className="mr-2" />
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </button>

            <span className="hidden" />

            {activeStep < 2 ? (
              <button
                type="button"
                onClick={nextStep}
                className={`px-5 py-2.5 rounded-xl font-medium flex items-center transition-colors ${isOutsideLagos ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-red-600 text-white hover:bg-red-700'}`}
              >
                Continue
                <FontAwesomeIcon icon={faChevronRight} className="ml-2" />
              </button>
            ) : (
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={loading}
                className={`px-5 py-2.5 rounded-xl font-medium flex items-center transition-colors ${loading ? 'bg-gray-400 cursor-not-allowed text-white opacity-50' : 'bg-green-600 text-white hover:bg-green-700'}`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                    Book Shipment
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewShipment;
